import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FileText, Sparkles } from 'lucide-react';
import { streamSse } from './sse';
import { providerLabel } from './ProviderToggle';
import { AiMarkdown } from './AiMarkdown';

const API = process.env.REACT_APP_BACKEND_URL;

export const EvidenceAnalysis = ({ caseId, evidences, provider, onDone }) => {
  const [selected, setSelected] = useState('');
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    const { data } = await axios.get(`${API}/api/cases/${caseId}/ai/analyses`, { withCredentials: true });
    setHistory(data);
  };

  useEffect(() => { loadHistory().catch(() => {}); }, [caseId]);
  useEffect(() => { if (!selected && evidences.length) setSelected(evidences[0].id); }, [evidences, selected]);

  const run = async () => {
    setRunning(true);
    setOutput('');
    try {
      await streamSse(`/api/evidence/${selected}/ai/analyze`, { provider }, (evt) => {
        if (evt.t) setOutput((prev) => prev + evt.t);
      });
      toast.success('Analysis saved');
      loadHistory();
      onDone?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  if (!evidences.length) {
    return <p className="text-sm text-gray-500" data-testid="ai-analysis-empty">Upload a document or image to the Evidence list first, then analyze it here.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <select value={selected} onChange={(e) => setSelected(e.target.value)} disabled={running} data-testid="ai-analysis-evidence-select"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
          {evidences.map((ev) => <option key={ev.id} value={ev.id}>{ev.filename}</option>)}
        </select>
        <button onClick={run} disabled={running || !selected} data-testid="ai-analysis-run-btn"
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          <Sparkles className="w-4 h-4" />
          <span>{running ? 'Analyzing...' : `Analyze with ${providerLabel(provider)}`}</span>
        </button>
      </div>
      {(output || running) && (
        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 max-h-[420px] overflow-y-auto" data-testid="ai-analysis-output">
          {output ? <AiMarkdown text={output} /> : <p className="text-sm text-purple-700 animate-pulse">Reading the file...</p>}
        </div>
      )}
      {history.length > 0 && (
        <div className="space-y-2" data-testid="ai-analysis-history">
          <h4 className="text-xs font-semibold text-gray-500 tracking-wide">Saved analyses</h4>
          {history.map((a) => (
            <details key={a.id} className="bg-gray-50 rounded-lg p-3" data-testid={`ai-analysis-item-${a.id}`}>
              <summary className="cursor-pointer text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-900">{a.filename}</span>
                <span className="text-xs text-gray-500">{providerLabel(a.provider)} · {new Date(a.created_at).toLocaleString()}</span>
              </summary>
              <div className="mt-3"><AiMarkdown text={a.content} /></div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};
